<template>
  <div
    tabindex="0"
    :data-id="objectModelRelation.client_id"
    :data-type="relationType"
    class="form-element"
    :class="relationshipClasses"
    v-on:focus="replaceIdInRoute($event, true)"
    @click.stop="replaceIdInRoute"
  >
    <div class="title-bar">
      <column class="row-title" v-if="objectModelRelation.can(ObjectModelRelationCapability.labelable)">
        <p>
          <strong id="relation_type_display_name">{{getDisplayName(relationType)}}</strong>
        </p>
      </column>
      <move-destroy
        :card="objectModelRelation"
        @remove:card="confirmDelete"
        :disabled="disabled"
        class="suffix"
      ></move-destroy>
    </div>
    <row>
      <column :size="isAccountRelationship ? 250 : 180">
        <object-model-relation-labelable
          :object-model-relation="objectModelRelation"
          :can-configure="!disabled"
          v-if="!objectModelRelation.hasExternalOrigin"
        ></object-model-relation-labelable>
        <label id="inverse_label_title" v-else>{{objectModelRelation.inverseLabelTitleOrDefault}}</label>
      </column>

      <column size="120" v-if="!isAccountRelationship">
        <p id="relation_type_friendly_name">{{getFriendlyName(relationType)}}</p>
      </column>

      <column size="180" v-if="!isAccountRelationship">
        <p id="inverse_relationship_title">
          <strong>{{objectModelRelation.inverseRelationshipTitle}}</strong>
        </p>
      </column>
    </row>
    <row v-if="!isAccountRelationship">
      <column size="285">
        <p>From Related Object Model(s)</p>
      </column>
      <column>
        <p v-html="relatedModelsSentence" id="related_models_sentence"></p>
      </column>
    </row>

    <confirm-delete-object-model-relationship-modal
      :state="modalStateFor(vueModalID.confirmDeleteObjectModelRelationship)"
      :index="vueModalID.confirmDeleteObjectModelRelationship"
      :relationship="objectModelRelation"
      @toggle-modal="hideModal(vueModalID.confirmDeleteObjectModelRelationship)"
      @remove="remove"
      v-if="modalStateFor(vueModalID.confirmDeleteObjectModelRelationship).isVisible">
    </confirm-delete-object-model-relationship-modal>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ObjectModelRelationDefinition from "../../../common/models/objectModelRelations/objectModelRelationDefinition";
import relationBaseMixin from "../mixins/relation-base-mixin";
import GloballySelected from "../store/modules/selected";
import { Hash } from "../../../common/interfaces/hashInterfaces";
import MoveDestroy from "../../../common/components/moveDestroy.vue";
import { ObjectModelRelationType } from "../../../common/enums/objectModelRelationType";
import ObjectModelRelationLabelable from "./object-model-relation-labelable.vue";
import { ObjectModelRelationCapability } from "../../../common/models/objectModelRelations/objectModelRelationCapability";
import { ArrayHelper } from "../../../common/helpers/arrayHelper";
import ObjectModel from "../../../common/models/objectModel";
import { RouteHelper } from "../../../common/helpers/routeHelper";
import disabledMixin from "../../../common/mixins/disabledMixin";
import routerMixin from "../../../common/mixins/routerMixin";
import routerSelectedMixinFactory from "../../../common/mixins/routerSelectedMixinFactory";
import ObjectModelRelation from "../../../common/models/objectModelRelations/objectModelRelation";
import { Route } from "../../../common/enums/route";
import ConfirmDeleteObjectModelRelationshipModal from "./confirm-delete-object-model-relationship-modal.vue";

export default Vue.extend({
  components: {
    MoveDestroy,
    ObjectModelRelationLabelable,
    ConfirmDeleteObjectModelRelationshipModal,
  },
  mixins: [
    relationBaseMixin,
    disabledMixin,
    routerMixin,
    routerSelectedMixinFactory("objectModelRelation", ".object-model-relation")
  ],
  data: function() {
    return {
      ObjectModelRelationCapability
    };
  },
  computed: {
    relationshipClasses(): Hash<boolean> {
      return {
        "object-model-relation": !this.disabled,
        active: this.objectModelRelation.isSelected,
        [this.getComponentClass(this.relationType)]: true
      };
    },
    relatedModelsSentence(): string {
      if (this.objectModelRelation.hasExternalOrigin) {
        return this.editModelRelationsLinkHtml(this.objectModelRelation.root_object_model);
      } else {
        if (this.objectModelRelation.related_object_models.length <= 0) {
          return "No type selected";
        } else {
          return ArrayHelper.toSentence<ObjectModel>(
            this.objectModelRelation.related_object_models,
            ", ",
            "or",
            model => {
              return this.editModelRelationsLinkHtml(model);
            }
          );
        }
      }
    },
    relationType(): ObjectModelRelationType {
      if (this.objectModelRelation.hasExternalOrigin) {
        return ObjectModelRelationDefinition.inverseRelationshipType(this.objectModelRelation.relation_type);
      } else {
        return this.objectModelRelation.relation_type;
      }
    },
    isAccountRelationship(): boolean {
      return this.objectModelRelation.isAccountRelationship;
    },
    modalMessageLabelTitle(): string {
      return this.objectModelRelation.labelTitleOr('Untitled Relationship');
    }
  },
  methods: {
    confirmDelete() {
      this.showModal(this.vueModalID.confirmDeleteObjectModelRelationship);
    },
    getComponentByType(type: ObjectModelRelationType): string {
      return ObjectModelRelationDefinition.getComponent(type);
    },
    getComponentClass(type: ObjectModelRelationType): string {
      return ObjectModelRelationDefinition.getComponentClass(type);
    },
    getDisplayName(type: ObjectModelRelationType): string {
      return ObjectModelRelationDefinition.getDisplayName(type);
    },
    getFriendlyName(type: ObjectModelRelationType): string {
      return ObjectModelRelationDefinition.getFriendlyName(type);
    },
    remove(event): void {
      this.$emit("remove", this.objectModelRelation);
    },
    linkHtml(model: ObjectModel, route: string): string {
      return `<a href="${route}" class="btn-link">${model.title}</a>`;
    },
    editModelLinkHtml(model: ObjectModel): string {
      return this.linkHtml(model, RouteHelper.path({ route: Route.editObjectModel, modelOrId: model }));
    },
    editModelRelationsLinkHtml(model: ObjectModel): string {
      return this.linkHtml(model, RouteHelper.path({ route: Route.editObjectModelRelationships, modelOrId: model }));
    },
    setSelected(objectModelRelation: ObjectModelRelation): void {
      GloballySelected.objectModelRelation.set(objectModelRelation);
    }
  }
});
</script>